import { useState, useEffect, useRef } from "react";
import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction } from '@solana/spl-token';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Wheel } from 'react-custom-roulette';

// Constants
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const MIN_BET_USD = 0.1;
const MAX_BET_USD = 100;

// Audio file paths
const AUDIO_PATHS = {
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  CHIP_PLACE: '/sounds/casino/coins_burst.mp3',
  WHEEL_SPIN: '/sounds/casino/drum_roll.mp3',
  WIN: '/win.wav',
  LOSE: '/sounds/casino/error.mp3'
};

// Roulette wheel order (European)
const WHEEL_ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];

// Number colors
const NUMBER_COLORS = {
  0: 'green',
  1: 'red', 2: 'black', 3: 'red', 4: 'black', 5: 'red', 6: 'black',
  7: 'red', 8: 'black', 9: 'red', 10: 'black', 11: 'black', 12: 'red',
  13: 'black', 14: 'red', 15: 'black', 16: 'red', 17: 'black', 18: 'red',
  19: 'red', 20: 'black', 21: 'red', 22: 'black', 23: 'red', 24: 'black',
  25: 'red', 26: 'black', 27: 'red', 28: 'black', 29: 'black', 30: 'red',
  31: 'black', 32: 'red', 33: 'black', 34: 'red', 35: 'black', 36: 'red'
};

// Betting payouts
const BET_PAYOUTS = {
  straight: 35,
  split: 17,
  street: 11,
  corner: 8,
  line: 5,
  dozen: 2,
  column: 2,
  color: 1,
  oddeven: 1,
  highlow: 1
};

// Prepare wheel data for react-custom-roulette
const wheelData = WHEEL_ORDER.map(num => ({
  option: num.toString(),
  style: {
    backgroundColor: NUMBER_COLORS[num] === 'red' ? '#DC2626' : 
                    NUMBER_COLORS[num] === 'black' ? '#111827' : 
                    '#059669',
    textColor: 'white'
  }
}));

// Icons
const ChipIcon = ({ value, className = "" }) => (
  <div className={`relative ${className}`}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <circle cx="50" cy="50" r="48" fill="currentColor" />
      <circle cx="50" cy="50" r="44" fill="none" stroke="white" strokeWidth="2" />
      <circle cx="50" cy="50" r="38" fill="none" stroke="white" strokeWidth="2" />
      <text x="50" y="55" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">
        {value >= 1 ? Math.floor(value) : value}
      </text>
    </svg>
  </div>
);

const WalletIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const SpeakerWaveIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
  </svg>
);

const SpeakerXMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
  </svg>
);

const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

// Professional Roulette Wheel Component
const ProfessionalRouletteWheel = ({ isSpinning, result, onSpinComplete }) => {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);

  useEffect(() => {
    if (isSpinning && result !== null) {
      const resultIndex = WHEEL_ORDER.indexOf(result);
      if (resultIndex !== -1) {
        setPrizeNumber(resultIndex);
        setMustSpin(true);
      }
    }
  }, [isSpinning, result]);

  const handleStopSpinning = () => {
    setMustSpin(false);
    if (onSpinComplete) {
      onSpinComplete();
    }
  };

  return (
    <div className="relative mx-auto" style={{ maxWidth: '320px' }}>
      <Wheel
        mustStartSpinning={mustSpin}
        prizeNumber={prizeNumber}
        data={wheelData}
        onStopSpinning={handleStopSpinning}
        backgroundColors={['#DC2626', '#111827']}
        textColors={['white']}
        fontSize={16}
        outerBorderColor="#FFD700"
        outerBorderWidth={8}
        innerRadius={30}
        innerBorderColor="#DAA520"
        innerBorderWidth={15}
        radiusLineColor="#FFD700"
        radiusLineWidth={2}
        spinDuration={4}
        startingOptionIndex={0}
        perpendicularText={false}
        textDistance={75}
      />
      
      {/* Top marker */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 pointer-events-none z-10">
        <div className="w-0 h-0 border-l-[16px] border-r-[16px] border-b-[28px] border-l-transparent border-r-transparent border-b-yellow-400 drop-shadow-2xl"></div>
      </div>
    </div>
  );
};

export default function QuantumRoulette() {
  // Mock price (replace with actual price fetching)
  const [arenaPrice] = useState(0.001);
  const [selectedChip, setSelectedChip] = useState(1);
  
  // Wallet
  const { connected, publicKey, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal();
  const { connection } = useConnection();
  
  // Game state
  const [bets, setBets] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [winAmount, setWinAmount] = useState(0);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hoveredBet, setHoveredBet] = useState(null);
  const [balance, setBalance] = useState(0);
  
  // Refs
  const audioRefs = useRef({});
  
  // Initialize audio
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      audioRefs.current[key] = audio;
    });
  }, []);
  
  // Fetch wallet balance
  useEffect(() => {
    if (connected && publicKey) {
      fetchBalance();
    }
  }, [connected, publicKey]);
  
  const fetchBalance = async () => {
    if (!publicKey || !connection) return;
    
    try {
      const tokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        publicKey
      );
      
      const balance = await connection.getTokenAccountBalance(tokenAccount);
      setBalance(balance.value.uiAmount || 0);
    } catch (error) {
      console.error("Error fetching balance:", error);
      setBalance(0);
    }
  };
  
  // Play sound
  const playSound = (soundKey) => {
    if (!soundEnabled || !audioRefs.current[soundKey]) return;
    const audio = audioRefs.current[soundKey].cloneNode();
    audio.play().catch(e => console.log("Audio prevented:", e));
  };
  
  // Calculate total bet
  const totalBet = bets.reduce((sum, bet) => sum + bet.amount, 0);
  const totalBetArena = totalBet / arenaPrice;
  
  // Place bet
  const placeBet = (type, numbers, position) => {
    if (!connected) {
      setVisible(true);
      return;
    }
    
    if (totalBet + selectedChip > MAX_BET_USD) {
      alert(`Maximum total bet is $${MAX_BET_USD}`);
      return;
    }
    
    playSound('CHIP_PLACE');
    
    // Check if bet already exists at this position
    const existingBetIndex = bets.findIndex(bet => 
      bet.type === type && JSON.stringify(bet.numbers) === JSON.stringify(numbers)
    );
    
    if (existingBetIndex >= 0) {
      // Add to existing bet
      const newBets = [...bets];
      newBets[existingBetIndex].amount += selectedChip;
      setBets(newBets);
    } else {
      // Create new bet
      setBets([...bets, {
        type,
        numbers,
        amount: selectedChip,
        position,
        id: Date.now()
      }]);
    }
  };
  
  // Clear all bets
  const clearBets = () => {
    playSound('BUTTON_CLICK');
    setBets([]);
  };
  
  // Undo last bet
  const undoLastBet = () => {
    playSound('BUTTON_CLICK');
    setBets(bets.slice(0, -1));
  };
  
  // Double all bets
  const doubleBets = () => {
    if (totalBet * 2 > MAX_BET_USD) {
      alert(`Cannot double - would exceed $${MAX_BET_USD} limit`);
      return;
    }
    playSound('CHIP_PLACE');
    setBets(bets.map(bet => ({ ...bet, amount: bet.amount * 2 })));
  };
  
  // Execute transaction
  const executeTransaction = async () => {
    if (!publicKey || !connection) throw new Error("Wallet not connected");
    
    const transaction = new Transaction();
    
    // Get token accounts
    const fromTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      publicKey
    );
    
    const toTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      PROJECT_WALLET
    );
    
    // Add transfer instruction
    const amountInLamports = Math.floor(totalBetArena * Math.pow(10, ARENA_DECIMALS));
    
    transaction.add(
      createTransferInstruction(
        fromTokenAccount,
        toTokenAccount,
        publicKey,
        amountInLamports
      )
    );
    
    // Send transaction
    const signature = await sendTransaction(transaction, connection);
    
    // Wait for confirmation with timeout handling
    try {
      const latestBlockhash = await connection.getLatestBlockhash();
      await connection.confirmTransaction({
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'finalized');
    } catch (confirmError) {
      // Check if transaction was actually successful despite timeout
      const signatureStatus = await connection.getSignatureStatus(signature);
      if (signatureStatus?.value?.confirmationStatus === 'finalized' || 
          signatureStatus?.value?.confirmationStatus === 'confirmed') {
        console.log("Transaction confirmed despite timeout:", signature);
        return signature;
      }
      throw confirmError;
    }
    
    return signature;
  };
  
  // Spin the wheel
  const spin = async () => {
    if (bets.length === 0) {
      alert('Please place a bet first!');
      return;
    }
    
    if (!connected || !publicKey) {
      setVisible(true);
      return;
    }
    
    if (totalBetArena > balance) {
      alert('Insufficient ARENA balance!');
      return;
    }
    
    try {
      playSound('WHEEL_SPIN');
      setIsSpinning(true);
      
      // Execute blockchain transaction
      const txSignature = await executeTransaction();
      console.log("Transaction submitted:", txSignature);
      
      // Don't wait for full confirmation, just a small delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, you would call your backend API here
      // to get the verifiably random result
      // For demo purposes, we'll use client-side random
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate result (replace with backend call)
      const result = Math.floor(Math.random() * 37);
      setLastResult(result);
      
      // Update balance
      await fetchBalance();
      
    } catch (error) {
      console.error("Error during spin:", error);
      alert("Transaction failed. Please try again.");
      setIsSpinning(false);
    }
  };
  
  // Handle spin complete
  const handleSpinComplete = () => {
    // Calculate winnings
    let totalWin = 0;
    const winningBets = [];
    
    bets.forEach(bet => {
      if (bet.numbers.includes(lastResult)) {
        const payout = BET_PAYOUTS[bet.type] || 0;
        const winAmount = bet.amount * (payout + 1);
        totalWin += winAmount;
        winningBets.push({ ...bet, winAmount });
      }
    });
    
    setWinAmount(totalWin);
    setIsSpinning(false);
    setShowResults(true);
    
    // Add to history
    setHistory([lastResult, ...history.slice(0, 11)]);
    
    // Play win/lose sound
    playSound(totalWin > 0 ? 'WIN' : 'LOSE');
    
    // Update balance if won
    if (totalWin > 0) {
      setTimeout(() => fetchBalance(), 2000);
    }
  };
  
  // Betting table component
  const BettingTable = () => (
    <div className="relative bg-gradient-to-b from-gray-800/50 to-gray-900/50 backdrop-blur-md rounded-2xl p-4 border border-gray-700/50">
      {/* Table layout */}
      <div className="relative">
        {/* Zero section */}
        <div className="absolute -left-12 top-0 h-full w-10">
          <button
            onClick={() => placeBet('straight', [0], { x: -1, y: 0 })}
            onMouseEnter={() => setHoveredBet('0')}
            onMouseLeave={() => setHoveredBet(null)}
            className={`h-full w-full bg-green-600 hover:bg-green-500 rounded-lg font-bold text-white flex items-center justify-center transition-all ${
              hoveredBet === '0' ? 'ring-2 ring-yellow-400' : ''
            }`}
          >
            0
          </button>
        </div>
        
        {/* Main grid */}
        <div className="grid grid-cols-12 gap-[2px]">
          {/* Numbers 1-36 */}
          {[3, 2, 1].map((row, rowIndex) => (
            <div key={row} className="contents">
              {Array.from({ length: 12 }, (_, col) => {
                const num = col * 3 + row;
                const color = NUMBER_COLORS[num];
                return (
                  <button
                    key={num}
                    onClick={() => placeBet('straight', [num], { x: col, y: rowIndex })}
                    onMouseEnter={() => setHoveredBet(num.toString())}
                    onMouseLeave={() => setHoveredBet(null)}
                    className={`h-12 font-bold rounded transition-all transform hover:scale-105 ${
                      color === 'red' 
                        ? 'bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600' 
                        : 'bg-gradient-to-br from-gray-900 to-black hover:from-gray-800 hover:to-gray-900'
                    } text-white text-sm relative ${
                      hoveredBet === num.toString() ? 'ring-2 ring-yellow-400 z-10' : ''
                    }`}
                  >
                    {num}
                    {/* Show chips on this number */}
                    {bets.filter(b => b.numbers.includes(num) && b.type === 'straight').map((bet, i) => (
                      <div key={i} className="absolute -top-1 -right-1 w-6 h-6">
                        <ChipIcon value={bet.amount} className="text-yellow-500" />
                      </div>
                    ))}
                  </button>
                );
              })}
            </div>
          ))}
          
          {/* Dozens */}
          <div className="contents">
            {['1st 12', '2nd 12', '3rd 12'].map((label, i) => (
              <button
                key={label}
                onClick={() => placeBet('dozen', Array.from({length: 12}, (_, j) => j + 1 + i * 12), { dozen: i })}
                className="col-span-4 h-10 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded transition-all"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Columns */}
        <div className="absolute -bottom-12 left-0 right-0 grid grid-cols-12 gap-[2px] h-10">
          {[1, 2, 3].map(col => (
            <button
              key={col}
              onClick={() => {
                const nums = Array.from({length: 12}, (_, i) => i * 3 + col);
                placeBet('column', nums, { column: col });
              }}
              className="col-span-4 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded transition-all"
            >
              2:1
            </button>
          ))}
        </div>
      </div>
      
      {/* Outside bets */}
      <div className="mt-16 grid grid-cols-6 gap-2">
        {/* 1-18 */}
        <button
          onClick={() => placeBet('highlow', Array.from({length: 18}, (_, i) => i + 1), { outside: 'low' })}
          className="py-3 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded-lg transition-all"
        >
          1-18
        </button>
        
        {/* EVEN */}
        <button
          onClick={() => placeBet('oddeven', Array.from({length: 18}, (_, i) => (i + 1) * 2), { outside: 'even' })}
          className="py-3 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded-lg transition-all"
        >
          EVEN
        </button>
        
        {/* RED */}
        <button
          onClick={() => {
            const redNums = Object.entries(NUMBER_COLORS).filter(([_, color]) => color === 'red').map(([num]) => parseInt(num));
            placeBet('color', redNums, { outside: 'red' });
          }}
          className="py-3 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-sm font-bold rounded-lg transition-all"
        >
          RED
        </button>
        
        {/* BLACK */}
        <button
          onClick={() => {
            const blackNums = Object.entries(NUMBER_COLORS).filter(([_, color]) => color === 'black').map(([num]) => parseInt(num));
            placeBet('color', blackNums, { outside: 'black' });
          }}
          className="py-3 bg-gradient-to-br from-gray-900 to-black hover:from-gray-800 hover:to-gray-900 text-white text-sm font-bold rounded-lg transition-all"
        >
          BLACK
        </button>
        
        {/* ODD */}
        <button
          onClick={() => placeBet('oddeven', Array.from({length: 18}, (_, i) => i * 2 + 1), { outside: 'odd' })}
          className="py-3 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded-lg transition-all"
        >
          ODD
        </button>
        
        {/* 19-36 */}
        <button
          onClick={() => placeBet('highlow', Array.from({length: 18}, (_, i) => i + 19), { outside: 'high' })}
          className="py-3 bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-bold rounded-lg transition-all"
        >
          19-36
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-6xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              window.history.back();
            }}
            className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400">
            QUANTUM ROULETTE
          </h1>
          
          <div className="flex items-center space-x-2">
            {/* Balance display */}
            {connected && (
              <div className="bg-black/30 backdrop-blur-sm px-3 py-2 rounded-xl border border-gray-800">
                <span className="text-xs text-gray-400">Balance</span>
                <p className="text-sm font-bold text-yellow-400">{balance.toFixed(2)} ARENA</p>
              </div>
            )}
            
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setSoundEnabled(!soundEnabled);
              }}
              className="bg-black/30 backdrop-blur-sm p-2 rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
            >
              {soundEnabled ? (
                <SpeakerWaveIcon className="h-5 w-5" />
              ) : (
                <SpeakerXMarkIcon className="h-5 w-5" />
              )}
            </button>
            
            <button
              onClick={() => connected ? null : setVisible(true)}
              className={`p-2 rounded-xl backdrop-blur-sm border transition-all ${
                connected 
                  ? 'bg-green-900/20 border-green-700/50 hover:border-green-600' 
                  : 'bg-red-900/20 border-red-700/50 hover:border-red-600'
              }`}
            >
              <WalletIcon className={`h-5 w-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left side - Wheel and history */}
          <div>
            {/* Roulette wheel */}
            <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-2xl p-6 border border-gray-700/50 mb-4">
              <ProfessionalRouletteWheel 
                isSpinning={isSpinning} 
                result={lastResult}
                onSpinComplete={handleSpinComplete}
              />
              
              {/* Last winning number */}
              {lastResult !== null && !isSpinning && (
                <div className="text-center mb-4">
                  <div className="text-sm text-gray-400 mb-2">Winning Number</div>
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full text-2xl font-bold ${
                    NUMBER_COLORS[lastResult] === 'red' 
                      ? 'bg-gradient-to-br from-red-600 to-red-700' 
                      : NUMBER_COLORS[lastResult] === 'black'
                      ? 'bg-gradient-to-br from-gray-900 to-black'
                      : 'bg-gradient-to-br from-green-600 to-green-700'
                  } text-white shadow-xl`}>
                    {lastResult}
                  </div>
                </div>
              )}
              
              {/* History */}
              {history.length > 0 && (
                <div>
                  <div className="text-xs text-gray-400 mb-2 text-center">Previous Results</div>
                  <div className="flex justify-center gap-1 flex-wrap">
                    {history.map((num, i) => (
                      <div
                        key={i}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          NUMBER_COLORS[num] === 'red' 
                            ? 'bg-red-600' 
                            : NUMBER_COLORS[num] === 'black'
                            ? 'bg-gray-900'
                            : 'bg-green-600'
                        } text-white`}
                      >
                        {num}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Chip selector */}
            <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-2xl p-4 border border-gray-700/50">
              <div className="text-sm text-gray-400 mb-3">Select Chip Value ($USD)</div>
              <div className="grid grid-cols-4 gap-2">
                {[0.1, 1, 5, 10, 25, 50, 100].map(value => (
                  <button
                    key={value}
                    onClick={() => {
                      playSound('BUTTON_CLICK');
                      setSelectedChip(value);
                    }}
                    className={`relative p-3 rounded-xl transition-all ${
                      selectedChip === value
                        ? 'bg-gradient-to-r from-yellow-600 to-amber-600 scale-105'
                        : 'bg-gray-800/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <ChipIcon 
                      value={value} 
                      className={selectedChip === value ? 'text-yellow-300' : 'text-gray-400'}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right side - Betting table */}
          <div>
            <BettingTable />
            
            {/* Current bets */}
            {bets.length > 0 && (
              <div className="mt-4 bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-2xl p-4 border border-gray-700/50">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-400">Current Bets</span>
                  <div className="flex gap-2">
                    <button
                      onClick={undoLastBet}
                      className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all"
                    >
                      Undo
                    </button>
                    <button
                      onClick={doubleBets}
                      className="text-xs px-3 py-1 bg-violet-700 hover:bg-violet-600 rounded-lg transition-all"
                    >
                      x2
                    </button>
                    <button
                      onClick={clearBets}
                      className="text-xs px-3 py-1 bg-red-700 hover:bg-red-600 rounded-lg transition-all"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {bets.map(bet => (
                    <div key={bet.id} className="flex justify-between items-center text-sm bg-gray-800/50 px-3 py-2 rounded-lg">
                      <span className="text-gray-300">
                        {bet.type === 'straight' && `Straight ${bet.numbers[0]}`}
                        {bet.type === 'color' && (bet.position.outside === 'red' ? 'Red' : 'Black')}
                        {bet.type === 'oddeven' && (bet.position.outside === 'odd' ? 'Odd' : 'Even')}
                        {bet.type === 'highlow' && (bet.position.outside === 'low' ? '1-18' : '19-36')}
                        {bet.type === 'dozen' && `${['1st', '2nd', '3rd'][bet.position.dozen]} Dozen`}
                        {bet.type === 'column' && `Column ${bet.position.column}`}
                      </span>
                      <span className="text-yellow-400 font-bold">${bet.amount}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-700 flex justify-between items-center">
                  <span className="text-gray-400">Total Bet:</span>
                  <div className="text-right">
                    <div className="text-yellow-400 font-bold text-lg">${totalBet.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">{totalBetArena.toFixed(2)} ARENA</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Spin button */}
            <button
              onClick={spin}
              disabled={bets.length === 0 || isSpinning || !connected}
              className={`w-full mt-4 py-4 rounded-xl font-bold text-lg tracking-wider transition-all duration-300 ${
                bets.length > 0 && connected && !isSpinning
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-xl transform hover:scale-[1.02]'
                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSpinning ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Spinning...
                </span>
              ) : connected ? (
                'SPIN THE WHEEL'
              ) : (
                'Connect Wallet to Play'
              )}
            </button>
          </div>
        </div>

        {/* Results Modal */}
        {showResults && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700">
              <h2 className="text-2xl font-bold text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                Round Complete
              </h2>
              
              <div className="text-center mb-6">
                <div className="text-sm text-gray-400 mb-2">Winning Number</div>
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-3xl font-bold ${
                  NUMBER_COLORS[lastResult] === 'red' 
                    ? 'bg-gradient-to-br from-red-600 to-red-700' 
                    : NUMBER_COLORS[lastResult] === 'black'
                    ? 'bg-gradient-to-br from-gray-900 to-black'
                    : 'bg-gradient-to-br from-green-600 to-green-700'
                } text-white shadow-2xl`}>
                  {lastResult}
                </div>
              </div>
              
              {winAmount > 0 ? (
                <div className="text-center mb-6">
                  <p className="text-lg text-green-400 mb-2">Congratulations!</p>
                  <p className="text-3xl font-bold text-yellow-400">
                    You won ${winAmount.toFixed(2)}!
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    ({(winAmount / arenaPrice).toFixed(2)} ARENA)
                  </p>
                </div>
              ) : (
                <div className="text-center mb-6">
                  <p className="text-lg text-gray-400">No wins this round</p>
                  <p className="text-sm text-gray-500 mt-2">Better luck next spin!</p>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowResults(false);
                  if (winAmount === 0) setBets([]);
                }}
                className="w-full py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold rounded-xl transition-all"
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
